import { SelectionModel } from '@angular/cdk/collections';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { debounceTime, distinctUntilChanged, firstValueFrom, Subject } from 'rxjs';
import {
  AddCaseDocumentsDialogComponent
} from 'src/app/dialogs/add-case-documents-dialog/add-case-documents-dialog.component';
import { Case, CaseDocument, CaseType } from 'src/app/models/case';
import { Paginator } from 'src/app/models/paginator';
import { CaseService } from 'src/app/services/case.service';
import { DocumentService } from 'src/app/services/document.service';
import { MessageService } from 'src/app/services/message.service';
import { DocumentsCaseComponent } from '../../documents-display/documents-case/documents-case.component';
import { Document, DocumentFilters } from 'src/app/models/document';
import { PageEvent } from '@angular/material/paginator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ClassWithPSAndDocuments, PSWithDocuments } from '../case-information/case-information.component';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { Router } from '@angular/router';
import { ConfirmationDialogComponent } from 'src/app/dialogs/confirmation-dialog/confirmation-dialog.component';
import { DocumentClass } from 'src/app/models/documentClass';
import { ProductService } from 'src/app/models/productService';


@Component({
  selector: 'app-case-renewal-proofs',
  templateUrl: './case-renewal-proofs.component.html',
  styleUrl: './case-renewal-proofs.component.scss'
})
export class CaseRenewalProofsComponent {
  CaseType = CaseType;

  @Input() case?: Case;
  @Input() readonly: boolean = false;

  @Input() productsServices?: ProductService[];
  @Input() trademarkProductsServices?: ProductService[];

  public phase?: { name: string };

  public filters: DocumentFilters = {
    query: undefined,
    approval: [],
    classNumber: [],
    productsServices: [],
    territories: [],
    documentClass: [],
    trademark: [],
    trademarkFamily: [],
    period: {
      start: undefined,
      end: undefined
    },
    tags: []
  }

  public count: number = 0;
  public noDocs: boolean = false;

  public pager: Paginator<CaseDocument> = {
    docs: [],
    page: 1,
    offset: 0,
    limit: 9999,
    totalDocs: 0,
    sort: '-createdAt'
  };
  filteredDocuments: CaseDocument[] = [];

  selection = new SelectionModel<CaseDocument>(true, []);

  @ViewChild('caseDocuments') caseDocuments?: DocumentsCaseComponent;

  @Output() refresh = new EventEmitter<any>();
  private readonly pagerSubject = new Subject<PageEvent>();

  @Input() classes: ClassWithPSAndDocuments[] = [];

  displayedColumns: string[] = ['name', 'nice', 'period', 'score', 'actions'];

  constructor(
    private _message: MessageService,
    private caseService: CaseService,
    private dialog: MatDialog,
    private documentService: DocumentService,
    private router: Router,
    private toastr: ToastrService,
    private translate: TranslateService
  ) {
    this.pagerSubject
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        takeUntilDestroyed()
      ).subscribe(async () => {
        await this.retrieveDocuments(false);
      });
  }

  init() {
    this.retrieveDocuments(true);
  }


  async retrieveDocuments(init: boolean) {
    try {
      this._message.emitChange("LOADING", "START");
      if (this.case) {
        this.pager = {
          ...this.pager,
          ...await this.caseService.retrieveDocuments(
            this.case._id!,
            this.pager.page,
            this.pager.limit,
            this.pager.offset,
            this.pager.sort,
            this.filters.territories,
            this.filters.classNumber,
            this.filters.productsServices,
            this.filters.trademark,
            this.filters.trademarkFamily,
            this.filters.documentClass,
            this.filters.period,
            this.filters.nonUse,
            this.filters.approval,
            this.filters.query
          )
        };
        this.filteredDocuments = this.pager.docs;
        this.case.documents = this.filteredDocuments.map(doc => ({ ref: doc._id!, score: doc.score }));
        this.count = this.pager.totalDocs;
        if (init && this.pager.totalDocs === 0) {
          this.noDocs = true;
        } else if (init && this.pager.totalDocs > 0) {
          this.noDocs = false;
        }
      }
      this._message.emitChange("LOADING", "END");
    } catch (err) {
      this._message.emitChange("LOADING", "END");
      this.toastr.error('ERRORS.GENERIC');
    }
  }

  _isMatchingDocument(document: CaseDocument, classNumber: number, productServiceId: string) {
    return document.productsServices
      && document.productsServices.map(ps => ps.classNumber).includes(classNumber)
      && document.productsServices.map(ps => ps.ref).includes(productServiceId)
      && (document.class && this._getDocumentsClassesForNiceClass(classNumber).includes(document.class))
  }

  async populateClassesAndPS() {
    await this.retrieveDocuments(true);
    this.classes.forEach((_class) => {
      _class.productsServices.forEach((ps) => {
        ps.documents = [];
        const matchingCaseDocuments = this.filteredDocuments.filter((caseDocument) => this._isMatchingDocument(caseDocument, _class.classNumber, ps.ref));
        if (!ps.documents) {
          ps.documents = matchingCaseDocuments;
        } else {
          ps.documents.push(...matchingCaseDocuments);
          ps.documents.sort((a, b) => (b.period ? moment(b.period.end) : moment("1900-01-01")).valueOf() - (a.period ? moment(a.period.end) : moment("1900-01-01")).valueOf())
        }
      })
    })
  }

  hasPS(niceClass: ClassWithPSAndDocuments) {
    return niceClass.productsServices && niceClass.productsServices.length > 0;
  }

  hasDocuments(productService: PSWithDocuments) {
    return productService.documents && productService.documents.length > 0;
  }

  getClassDocuments(niceClass: ClassWithPSAndDocuments) {
    const documents: (string | undefined)[] = [];
    niceClass.productsServices.forEach((ps) => {
      ps.documents?.forEach((caseDocument) => {
        if (!documents.includes(caseDocument._id)) {
          documents.push(caseDocument._id)
        }
      })
    })
    return documents.length
  }

  getClassProblems(niceClass: ClassWithPSAndDocuments) {
    return niceClass.productsServices.filter((ps) => !ps.documents || ps.documents?.length === 0).length;
  }


  async openUploadDialog() {
    this.documentService.openUploadDialog([], this.case);
  }

  _getDocumentsClassesForNiceClass(niceClass: number) {
    const baseDocumentClasses = [DocumentClass.BOOKLET, DocumentClass.CATALOG, DocumentClass.PICTURE, DocumentClass.WEBSITE_SCREENSHOT, DocumentClass.ONLINE_STORE_SCREENSHOT];
    if (niceClass <= 34) {
      return baseDocumentClasses;
    }
    return [...baseDocumentClasses, DocumentClass.INVOICE];
  }

  async openLinkDialog(niceClass?: number, productService?: string) {
    const config: MatDialogConfig = {
      panelClass: 'dialog-container',
      height: "calc(100% - 30px)",
      width: "calc(100% - 30px)",
      maxWidth: "100%",
      maxHeight: "100%",
      autoFocus: false,
      data: {
        case: this.case,
        niceClass,
        productService,
        documentClasses: niceClass ? this._getDocumentsClassesForNiceClass(niceClass) : undefined
      }
    }
    const dialog = this.dialog.open(AddCaseDocumentsDialogComponent, config);
    const result: { linked: boolean } = await firstValueFrom(dialog.afterClosed());
    if (result && result.linked && this.case?._id) {
      this.init();
      this.refresh.emit();
    }
  }

  async unlink(document: Document) {

    if (this.case) {
      try {
        const config: MatDialogConfig = {
          panelClass: 'dialog-container',
          width: '480px',
          data: {
            title: 'CASE.RENEWAL_UNLINK_PROOF_TITLE',
            text: this.translate.instant('CASE.RENEWAL_UNLINK_PROOF_TEXT', { documentName: document.name, caseReference: this.case.identifier || this.case.reference }),
            button: {
              text: 'CASE.UNLINK',
              class: 'danger-button'
            }
          }
        }
        const dialog = this.dialog.open(ConfirmationDialogComponent, config);
        const result: { confirmed: boolean } = await firstValueFrom(dialog.afterClosed());
        if (result && result.confirmed && this.case && this.case?._id && document._id) {
          this._message.emitChange("LOADING", "START");
          await this.caseService.unlink(this.case._id, [document._id]);
          this.init();
          this.refresh.emit();
          this._message.emitChange("LOADING", "END");
          this.toastr.success(this.translate.instant('CASE.UNLINK_PROOF_SUCCESS'));
        }
      } catch (err) {
        this._message.emitChange("LOADING", "END");
        this.toastr.error('ERRORS.GENERIC');
      }
    }

  }

  _setDocumentFiltersBasedOnCase(productServiceRef: string) {
    if (this.case) {
      const found = this.trademarkProductsServices?.find((tps) => tps._id === productServiceRef);
      this.documentService.filters = {
        query: undefined,
        approval: [],
        classNumber: this.case?.niceClassification,
        productsServices: found ? [found] : [],
        territories: [this.case.trademark.countryOfDesignation],
        documentClass: [],
        // _id is required for the document page metadata filters to match the
        // case trademark against the stored metadata value (ref first)
        trademark: [
          {
            ...this.case.trademark,
            _id: this.case.trademark.ref,
          },
        ],
        trademarkFamily: [],
        period: {
          start: moment(this.case.timePeriod.start),
          end: moment(this.case.timePeriod.end)
        },
        tags: []
      }
    }
  }

  toDocument(productServiceRef: string, document: CaseDocument) {
    this._setDocumentFiltersBasedOnCase(productServiceRef);
    this.router.navigateByUrl(`/documents/${document._id}`, {
      state: {
        from: "CASE",
        fromId: this.case?._id
      }
    })
  }

  documentIcon(document: CaseDocument) {
    return this.documentService.icon(document);
  }

  formatSize(document: CaseDocument) {
    return this.documentService.size(document);
  }

  formatPeriod(document: CaseDocument) {
    return this.documentService.period(document);
  }

  formatDate(date: Moment) {
    return moment(date).format('ll');
  }

}
